/**
 * Wiki backend API client.
 * Публичные: GET /api/wiki/*. Админка: POST|PUT|DELETE /api/wiki-admin/* с JWT (Bearer).
 * Запросы идут напрямую на WIKI_CONFIG.apiUrl (прокси не используется).
 */
import { WIKI_CONFIG } from '../config';

const BASE = (WIKI_CONFIG.apiUrl || '').replace(/\/$/, '');

function getAuthHeaders(includeContentType = true) {
  const token = localStorage.getItem('wiki_admin_token');
  const headers = {};
  if (includeContentType) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function clearAuthAndRedirectToLogin() {
  localStorage.removeItem('wiki_admin_token');
  localStorage.removeItem('wiki_admin_token_expiry');
  localStorage.removeItem('wiki_admin_username');
  localStorage.removeItem('wiki_admin_nickname');
  localStorage.removeItem('wiki_admin_id');
  if (typeof window !== 'undefined') {
    window.location.replace(window.location.origin + '/wiki/admin/login');
  }
}

async function handleResponse(res) {
  if (res.status === 401) {
    clearAuthAndRedirectToLogin();
    const err = new Error('Unauthorized');
    err.status = 401;
    throw err;
  }
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {
    // бэкенд может вернуть HTML (например Whitelabel 404) — не падаем на парсе
  }
  if (!res.ok) {
    const message = data?.message || data?.detail || data?.error || res.statusText || `HTTP ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    if (res.status === 403) {
      err.isAdminForbidden = true;
    }
    throw err;
  }
  return data;
}

// ——— Public (no auth) ———
export async function getPages(params = {}) {
  const q = new URLSearchParams();
  if (params.page != null) q.set('page', params.page);
  if (params.size != null) q.set('size', params.size);
  if (params.category) q.set('category', params.category);
  if (params.search) q.set('search', params.search);
  if (params.sort != null) q.set('sort', params.sort); // backend: popular | new
  const query = q.toString();
  const res = await fetch(`${BASE}/api/wiki/pages${query ? '?' + query : ''}`);
  return handleResponse(res);
}

export async function getPageBySlug(slug) {
  const res = await fetch(`${BASE}/api/wiki/pages/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  return handleResponse(res);
}

/** GET /api/wiki/pages/{slug}/history — история изменений страницы (для автора/редактора). */
export async function getPageHistory(slug, params = {}) {
  const page = params.page != null ? params.page : 0;
  const size = params.size != null ? params.size : 20;
  const q = new URLSearchParams({ page: String(page), size: String(size) });
  const res = await fetch(`${BASE}/api/wiki/pages/${encodeURIComponent(slug)}/history?${q}`);
  if (res.status === 404) return { content: [] };
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {}
  if (!res.ok) {
    const err = new Error(data?.message || data?.detail || data?.error || res.statusText || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data || { content: [] };
}

export async function getCategories() {
  const res = await fetch(`${BASE}/api/wiki/categories`);
  return handleResponse(res);
}

/** Категория с деревом подкатегорий (GET /api/wiki/categories/{id}) */
export async function getCategory(id) {
  const res = await fetch(`${BASE}/api/wiki/categories/${id}`);
  if (res.status === 404) return null;
  return handleResponse(res);
}

// Не в API — заглушка
export async function getPageById(id) {
  const data = await getPages({ size: 1000 });
  const list = data?.content ?? [];
  const page = list.find((p) => String(p.id) === String(id));
  return page || null;
}

export async function incrementViews() {
  // В API нет эндпоинта — не вызываем
}

// ——— Admin (JWT). Backend: JSON body (PageRequest / CategoryRequest). ———
export async function createPage(data) {
  const categoryId = Number(data.categoryId ?? data.category);
  if (!Number.isFinite(categoryId)) {
    throw new Error('Выберите категорию (бэкенд ожидает categoryId — число)');
  }
  const body = {
    title: data.title,
    slug: data.slug,
    contentHtml: data.content ?? data.contentHtml ?? '',
    categoryId,
  };
  const res = await fetch(`${BASE}/api/wiki-admin/pages`, {
    method: 'POST',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function updatePage(id, data) {
  const categoryId = Number(data.categoryId ?? data.category);
  if (!Number.isFinite(categoryId)) {
    throw new Error('Выберите категорию (бэкенд ожидает categoryId — число)');
  }
  const body = {
    title: data.title,
    slug: data.slug ?? '',
    contentHtml: data.content ?? data.contentHtml ?? '',
    categoryId,
  };
  const res = await fetch(`${BASE}/api/wiki-admin/pages/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function deletePage(id) {
  const res = await fetch(`${BASE}/api/wiki-admin/pages/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(false),
  });
  if (res.status === 200 || res.status === 204) return true;
  return handleResponse(res);
}

export async function addCategory(data) {
  const rawSlug = data.slug ?? data.id ?? (data.name ?? '');
  const slug =
    typeof rawSlug === 'string'
      ? rawSlug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      : String(rawSlug ?? '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  const body = {
    name: String(data.name ?? '').trim(),
    slug,
    parentId: data.parentId != null && data.parentId !== '' ? Number(data.parentId) : null,
  };
  let res;
  try {
    res = await fetch(`${BASE}/api/wiki-admin/categories`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new Error(
      'Сервер не ответил. Запустите бэкенд (wiki JAR) на порту 8080 и перезапустите npm start.'
    );
  }
  return handleResponse(res);
}

export async function updateCategory(id, data) {
  const nameStr = String(data.name ?? '').trim();
  const rawSlug = data.slug ?? data.name ?? '';
  const slug =
    typeof rawSlug === 'string'
      ? rawSlug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      : String(rawSlug ?? '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  const body = {
    name: nameStr,
    slug,
    parentId: data.parentId != null && data.parentId !== '' ? Number(data.parentId) : null,
  };
  if (data.order !== undefined && data.order !== null) {
    body.displayOrder = Number(data.order);
  }
  const res = await fetch(`${BASE}/api/wiki-admin/categories/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function deleteCategory(id) {
  const res = await fetch(`${BASE}/api/wiki-admin/categories/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(false),
  });
  if (res.status === 200 || res.status === 204) return true;
  return handleResponse(res);
}

/**
 * Загрузка изображения для вики. POST multipart, ответ: { id, url } или { imageUrl }, { path } и т.д.
 * Content-Type не задаём — браузер подставит boundary для FormData.
 * Возвращает объект с полем url: подходит для <img src="url"> (например /api/wiki/images/5).
 */
export async function uploadImage(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE}/api/wiki-admin/images`, {
    method: 'POST',
    headers: getAuthHeaders(false),
    body: formData,
  });
  const data = await handleResponse(res);
  let url = data?.url ?? data?.imageUrl ?? data?.path ?? '';
  if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
    try {
      const path = new URL(url).pathname;
      if (path) url = path;
    } catch (_) {}
  }
  return { ...data, url };
}

// ——— Admin: пользователи (UserRequest: login, password, displayName). ———
export async function getUsers() {
  const res = await fetch(`${BASE}/api/wiki-admin/users`, {
    method: 'GET',
    headers: getAuthHeaders(true),
  });
  return handleResponse(res);
}

export async function createUser(data) {
  const body = {
    login: String(data.login ?? '').trim(),
    password: data.password ?? '',
    displayName: String(data.displayName ?? '').trim(),
    role: data.role || 'ROLE_EDITOR',
    permissions: data.permissions || [],
  };
  const res = await fetch(`${BASE}/api/wiki-admin/users`, {
    method: 'POST',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function getUser(id) {
  const res = await fetch(`${BASE}/api/wiki-admin/users/${id}`, {
    method: 'GET',
    headers: getAuthHeaders(true),
  });
  if (res.status === 404) return null;
  return handleResponse(res);
}

export async function updateUser(id, data) {
  const body = {};
  if (data.login != null) body.login = String(data.login).trim();
  if (data.password) body.password = data.password;
  if (data.displayName != null) body.displayName = String(data.displayName).trim();
  if (data.permissions != null) body.permissions = data.permissions;
  const res = await fetch(`${BASE}/api/wiki-admin/users/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

/** Изменить пароль пользователя (admin). */
export async function changeUserPassword(id, newPassword) {
  const body = { password: newPassword };
  const res = await fetch(`${BASE}/api/wiki-admin/users/${id}/password`, {
    method: 'PUT',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  });
  // Если эндпоинт /password не существует — fallback на updateUser
  if (res.status === 404 || res.status === 405) {
    return updateUser(id, { password: newPassword });
  }
  return handleResponse(res);
}

/** Обновить права доступа пользователя (admin). */
export async function updateUserPermissions(id, permissions) {
  // Пробуем специальный эндпоинт, fallback — updateUser
  const body = { permissions };
  const res = await fetch(`${BASE}/api/wiki-admin/users/${id}/permissions`, {
    method: 'PUT',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  });
  if (res.status === 404 || res.status === 405) {
    return updateUser(id, { permissions });
  }
  return handleResponse(res);
}

export async function deleteUser(id) {
  const res = await fetch(`${BASE}/api/wiki-admin/users/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(false),
  });
  if (res.status === 200 || res.status === 204) return true;
  return handleResponse(res);
}

// GET /api/wiki/history — постраничная история (публичный эндпоинт, без сброса токена при 401).
export async function getHistory(params = {}) {
  const page = params.page != null ? params.page : 0;
  const size = params.size != null ? params.size : 100;
  const q = new URLSearchParams({ page: String(page), size: String(size) });
  const res = await fetch(`${BASE}/api/wiki/history?${q}`);
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {}
  if (!res.ok) {
    const err = new Error(data?.message || data?.detail || data?.error || res.statusText || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function getVersions() {
  return [];
}

export async function restoreVersion() {
  return null;
}

// ——— Аналитика поиска (хранение только на бэке). ———
const WIKI_SESSION_STORAGE_KEY = 'wiki-session-id';

function getWikiSessionId() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    let id = window.sessionStorage.getItem(WIKI_SESSION_STORAGE_KEY);
    if (!id) {
      if (window.crypto?.randomUUID) {
        id = window.crypto.randomUUID();
      } else {
        // Простейший fallback, если randomUUID недоступен
        id = `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
      }
      window.sessionStorage.setItem(WIKI_SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * Сохранить пустой поисковый запрос.
 * Бэкенд ожидает SearchLogRequest: { query, resultCount, sessionId? }.
 */
export async function logEmptySearch(query, resultCount = 0) {
  const q = String(query || '').trim();
  if (!q) return;
  const count = Number.isFinite(resultCount) ? Number(resultCount) : 0;
  const sessionId = getWikiSessionId();
  const payload = {
    query: q,
    resultCount: count,
  };
  if (sessionId) {
    payload.sessionId = sessionId;
  }
  try {
    await fetch(`${BASE}/api/wiki/search-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (_) {}
}

/** GET пустых запросов с бэка (админка, JWT). Возвращает PageSearchLog. */
export async function getEmptySearchQueries(params = {}) {
  const page = params.page != null ? params.page : 0;
  const size = params.size != null ? params.size : 50;
  const minSearchCount =
    params.minSearchCount != null ? params.minSearchCount : undefined;
  const q = new URLSearchParams();
  q.set('page', String(page));
  q.set('size', String(size));
  if (minSearchCount != null) {
    q.set('minSearchCount', String(minSearchCount));
  }
  const res = await fetch(
    `${BASE}/api/wiki-admin/analytics/empty-queries?${q.toString()}`,
    {
      method: 'GET',
      headers: getAuthHeaders(false),
    }
  );
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  if (!data || typeof data !== 'object') return null;
  const pageData = {
    content: Array.isArray(data.content) ? data.content : [],
    totalPages: data.totalPages ?? 0,
    totalElements: data.totalElements ?? 0,
    size: data.size ?? size,
    number: data.number ?? page,
    first: data.first ?? page === 0,
    last:
      data.last ??
      (data.totalPages != null ? page >= data.totalPages - 1 : false),
    numberOfElements: data.numberOfElements ?? (data.content?.length ?? 0),
  };
  return pageData;
}

/** DELETE одной записи лога поиска по id. */
export async function deleteSearchLog(id) {
  const res = await fetch(
    `${BASE}/api/wiki-admin/analytics/search-logs/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(false),
    }
  );
  if (!res.ok) {
    return handleResponse(res);
  }
  return true;
}

/** DELETE логов поиска. Если before задан, удаляет до указанной даты. */
export async function deleteSearchLogs(before) {
  const q = new URLSearchParams();
  if (before) q.set('before', before);
  const res = await fetch(
    `${BASE}/api/wiki-admin/analytics/search-logs${
      q.toString() ? `?${q.toString()}` : ''
    }`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(false),
    }
  );
  if (!res.ok) {
    return handleResponse(res);
  }
  return true;
}

/** Топ страниц по просмотрам (GET с бэка). При ошибке — null. */
export async function getTopPages(limit = 10) {
  const res = await fetch(
    `${BASE}/api/wiki-admin/analytics/top-pages?limit=${Number(limit) || 10}`,
    { method: 'GET', headers: getAuthHeaders(false) }
  );
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  return data?.content ?? data?.items ?? (Array.isArray(data) ? data : null);
}

/** Страницы, давно не обновлявшиеся (GET с бэка). Параметр days — порог в днях (по умолчанию 7). При ошибке — null. */
export async function getStalePages(days = 7) {
  const d = Number(days) || 7;
  const res = await fetch(
    `${BASE}/api/wiki-admin/analytics/stale-pages?days=${d}`,
    { method: 'GET', headers: getAuthHeaders(false) }
  );
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  return data?.content ?? data?.items ?? (Array.isArray(data) ? data : null);
}

// ——— Метки карты (Map Markers). Хранятся в localStorage как fallback, бэкенд опционален. ———
const MAP_MARKERS_KEY = 'map_markers';

/** Загрузить метки: сначала пробуем бэкенд, при ошибке — localStorage. */
export async function getMapMarkers() {
  try {
    const res = await fetch(`${BASE}/api/wiki/map-markers`);
    if (res.ok) {
      const data = await res.json().catch(() => null);
      if (Array.isArray(data)) return data;
    }
  } catch (_) {}
  // Fallback: localStorage
  try {
    const raw = localStorage.getItem(MAP_MARKERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

/** Сохранить все метки (admin). Пробуем бэкенд, при ошибке — localStorage. */
export async function saveMapMarkers(markers) {
  try {
    const res = await fetch(`${BASE}/api/wiki-admin/map-markers`, {
      method: 'PUT',
      headers: getAuthHeaders(true),
      body: JSON.stringify(markers),
    });
    if (res.ok) {
      // Синхронизируем localStorage
      localStorage.setItem(MAP_MARKERS_KEY, JSON.stringify(markers));
      return true;
    }
  } catch (_) {}
  // Fallback: сохраняем только в localStorage
  localStorage.setItem(MAP_MARKERS_KEY, JSON.stringify(markers));
  return true;
}

// ——— Турниры (Tournaments). Хранятся в localStorage как fallback, бэкенд опционален. ———
const TOURNAMENTS_KEY = 'wiki_tournaments';

function getTournamentsFromStorage() {
  try {
    const raw = localStorage.getItem(TOURNAMENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

function saveTournamentsToStorage(tournaments) {
  localStorage.setItem(TOURNAMENTS_KEY, JSON.stringify(tournaments));
}

/** Получить список всех турниров. */
export async function getTournaments() {
  try {
    const res = await fetch(`${BASE}/api/wiki/tournaments`);
    if (res.ok) {
      const data = await res.json().catch(() => null);
      if (Array.isArray(data)) return data;
    }
  } catch (_) {}
  return getTournamentsFromStorage();
}

/** Получить турнир по id. */
export async function getTournamentById(id) {
  try {
    const res = await fetch(`${BASE}/api/wiki/tournaments/${encodeURIComponent(id)}`);
    if (res.ok) {
      const data = await res.json().catch(() => null);
      if (data) return data;
    }
  } catch (_) {}
  const list = getTournamentsFromStorage();
  return list.find((t) => String(t.id) === String(id)) || null;
}

/** Создать турнир (admin). */
export async function createTournament(data) {
  try {
    const res = await fetch(`${BASE}/api/wiki-admin/tournaments`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify(data),
    });
    if (res.ok) {
      const created = await res.json().catch(() => null);
      if (created) return created;
    }
  } catch (_) {}
  // Fallback: localStorage
  const list = getTournamentsFromStorage();
  const newTournament = {
    ...data,
    id: Date.now(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  list.push(newTournament);
  saveTournamentsToStorage(list);
  return newTournament;
}

/** Обновить турнир (admin). */
export async function updateTournament(id, data) {
  try {
    const res = await fetch(`${BASE}/api/wiki-admin/tournaments/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: getAuthHeaders(true),
      body: JSON.stringify(data),
    });
    if (res.ok) {
      const updated = await res.json().catch(() => null);
      if (updated) return updated;
    }
  } catch (_) {}
  // Fallback: localStorage
  const list = getTournamentsFromStorage();
  const idx = list.findIndex((t) => String(t.id) === String(id));
  if (idx === -1) throw new Error('Турнир не найден');
  const updated = { ...list[idx], ...data, id, updatedAt: new Date().toISOString() };
  list[idx] = updated;
  saveTournamentsToStorage(list);
  return updated;
}

/** Удалить турнир (admin). */
export async function deleteTournament(id) {
  try {
    const res = await fetch(`${BASE}/api/wiki-admin/tournaments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(false),
    });
    if (res.ok) {
      const list = getTournamentsFromStorage();
      saveTournamentsToStorage(list.filter((t) => String(t.id) !== String(id)));
      return true;
    }
  } catch (_) {}
  // Fallback: localStorage
  const list = getTournamentsFromStorage();
  saveTournamentsToStorage(list.filter((t) => String(t.id) !== String(id)));
  return true;
}

// Public quest definitions; AbortSignal cancels navigation-away requests.
export async function getQuestCatalog(signal) {
  const response = await fetch(`${BASE}/api/wiki/quests`, { signal });
  if (!response.ok) throw new Error('Задания временно недоступны');
  const data = await response.json();
  if (!Array.isArray(data?.quests)) throw new Error('Не удалось прочитать задания');
  return data;
}
